import { Download, Sparkles, ArrowRight, Clock, Tag } from "lucide-react";
import { useEffect, useState } from "react";
import useScrollReveal from "@/hooks/useScrollReveal";
import { apiGet } from "@/lib/api";

// TM7-Kunden-Angebote – dynamisch aus dem Backend, mit automatischer Ablauf-Logik

const HIDE_AFTER_DAYS = 7;

const mapOffer = (o) => ({
  id: o.id,
  title: o.title,
  subtitle: o.subtitle,
  tagline: o.tagline,
  price: o.price,
  priceNote: o.price_note,
  savings: o.savings,
  highlight: o.highlight,
  badge: o.badge,
  icon: o.icon,
  validFromIso: o.valid_from,
  validUntilIso: o.valid_until,
  pdfUrl: o.pdf_url,
  features: o.features || [],
});

const statusOf = (offer, today) => {
  const from = parseLocalDate(offer.validFromIso);
  const until = parseLocalDate(offer.validUntilIso);
  if (today < from) return "upcoming";
  if (today <= until) return "current";
  const daysSinceExpiry = Math.round((today - until) / (1000 * 60 * 60 * 24));
  if (daysSinceExpiry > HIDE_AFTER_DAYS) return "hidden";
  return "expired";
};

const parseLocalDate = (iso) => {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d);
};
const startOfToday = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

const daysUntil = (iso) => {
  const d = parseLocalDate(iso);
  return Math.round((d - startOfToday()) / (1000 * 60 * 60 * 24));
};

const formatDate = (iso) => {
  const d = parseLocalDate(iso);
  return `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}.${d.getFullYear()}`;
};

const AngeboteSection = () => {
  const today = startOfToday();
  const [offers, setOffers] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const sectionRef = useScrollReveal(offers.length);

  useEffect(() => {
    apiGet("/content/angebote")
      .then((data) => setOffers(data.map(mapOffer)))
      .catch(() => setOffers([]))
      .finally(() => setLoaded(true));
  }, []);

  const visibleOffers = offers
    .map((o) => ({ ...o, status: statusOf(o, today) }))
    .filter((o) => o.status !== "hidden")
    .sort((a, b) => {
      const order = { current: 0, upcoming: 1, expired: 2 };
      return order[a.status] - order[b.status];
    });

  if (!loaded) return null;
  if (visibleOffers.length === 0) return null;

  // Min days left across all current offers (für zentrale Countdown-Anzeige)
  const currentOffers = visibleOffers.filter(o => o.status === "current");
  const minDaysLeft = currentOffers.length > 0
    ? Math.min(...currentOffers.map(o => daysUntil(o.validUntilIso)))
    : null;

  return (
    <section
      id="angebote"
      data-testid="angebote-section"
      className="section-padding relative overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900"
      ref={sectionRef}
    >
      <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-emerald-400/15 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-amber-400/10 rounded-full blur-3xl pointer-events-none" />

      <div className="container-custom relative z-10">
        <div className="text-center max-w-2xl mx-auto mb-14 reveal">
          <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-emerald-400 mb-4">
            <Tag className="w-3.5 h-3.5" /> Nur für kurze Zeit
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight mb-5 font-[Outfit]">
            Aktuelle TM7-Angebote
          </h2>
          <p className="text-slate-300 text-lg leading-relaxed mb-6">
            Zeitlich begrenzte Vorteilspakete direkt vom Hersteller – hol dir dein TM7 jetzt zum Sonderpreis.
          </p>
          {minDaysLeft !== null && minDaysLeft <= 10 && (
            <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-amber-500/20 border border-amber-400/30 text-amber-200 text-sm font-bold animate-pulse">
              <Clock className="w-4 h-4" />
              Nur noch {minDaysLeft} {minDaysLeft === 1 ? "Tag" : "Tage"} gültig
            </div>
          )}
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto reveal reveal-delay-1">
          {visibleOffers.map((offer) => {
            const isCurrent = offer.status === "current";
            const isExpired = offer.status === "expired";
            const isUpcoming = offer.status === "upcoming";
            const daysLeft = isCurrent ? daysUntil(offer.validUntilIso) : 0;

            return (
              <div
                key={offer.id}
                data-testid={`angebot-card-${offer.id}`}
                className={`relative rounded-3xl p-7 border flex flex-col transition-all duration-300 ${
                  isExpired
                    ? "bg-slate-800/40 border-slate-700/50 opacity-60"
                    : offer.highlight
                    ? "bg-gradient-to-br from-emerald-500/15 via-slate-800/80 to-slate-800/80 border-emerald-400/30 hover:border-emerald-400/60 hover:-translate-y-2 shadow-2xl shadow-emerald-500/10"
                    : "bg-slate-800/60 backdrop-blur-sm border-slate-700/50 hover:border-emerald-400/40 hover:-translate-y-1"
                }`}
              >
                {/* Badge oben */}
                {isCurrent && offer.highlight && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1.5 bg-gradient-to-r from-emerald-400 to-emerald-500 rounded-full shadow-lg shadow-emerald-500/40 whitespace-nowrap">
                    <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-white flex items-center gap-1">
                      <Sparkles className="w-3 h-3" /> {offer.badge}
                    </span>
                  </div>
                )}
                {isCurrent && !offer.highlight && (
                  <div className="absolute top-4 right-4 px-3 py-1 rounded-full bg-slate-700/50 backdrop-blur-sm border border-slate-600/50">
                    <span className="text-[9px] font-bold uppercase tracking-wider text-slate-300">
                      {offer.badge}
                    </span>
                  </div>
                )}
                {isExpired && (
                  <div className="absolute -top-3 right-3 px-3 py-1 bg-stone-600 rounded-full">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-white flex items-center gap-1">
                      <Clock className="w-3 h-3" /> Abgelaufen
                    </span>
                  </div>
                )}
                {isUpcoming && (
                  <div className="absolute -top-3 right-3 px-3 py-1 bg-amber-500 rounded-full">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-white">
                      Bald verfügbar
                    </span>
                  </div>
                )}

                <div className="mb-5">
                  <div className="text-4xl mb-3">{offer.icon}</div>
                  <div className="text-xs font-bold uppercase tracking-[0.15em] text-emerald-400 mb-1.5">
                    {offer.subtitle}
                  </div>
                  <h3 className="text-2xl font-extrabold text-white font-[Outfit] leading-tight mb-1.5">
                    {offer.title}
                  </h3>
                  <p className="text-sm text-slate-400 italic">
                    „{offer.tagline}"
                  </p>
                </div>

                <div className="mb-5 py-4 border-y border-slate-700/50">
                  <div className="flex items-baseline gap-2 mb-1">
                    <span className="text-4xl font-extrabold text-white font-[Outfit]">
                      {offer.price} €
                    </span>
                    {offer.savings && (
                      <span className="text-xs font-bold text-emerald-400 bg-emerald-400/10 px-2 py-1 rounded-full border border-emerald-400/30">
                        – {offer.savings}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-400">{offer.priceNote}</div>
                </div>

                <ul className="space-y-2.5 mb-6 flex-1">
                  {offer.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm text-slate-300">
                      <div className="shrink-0 w-4 h-4 rounded-full bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center mt-0.5">
                        <span className="text-emerald-400 text-[9px] font-bold">✓</span>
                      </div>
                      <span className="leading-snug">{f}</span>
                    </li>
                  ))}
                </ul>

                {/* Countdown */}
                {isCurrent && (
                  <div className="flex items-center gap-1.5 mb-3 text-xs">
                    <Clock className="w-3.5 h-3.5 text-amber-400" />
                    <span className="text-slate-400">Gültig bis</span>
                    <span className="font-bold text-amber-300">{formatDate(offer.validUntilIso)}</span>
                    {daysLeft <= 7 && (
                      <span className="ml-auto text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 border border-amber-400/30">
                        {daysLeft} T
                      </span>
                    )}
                  </div>
                )}

                {/* CTAs */}
                {isExpired ? (
                  <div className="w-full py-3 rounded-full bg-slate-700/50 text-slate-500 text-sm font-semibold text-center cursor-not-allowed">
                    Angebot abgelaufen
                  </div>
                ) : (
                  <div className="space-y-2 mt-auto">
                    <a
                      href={offer.pdfUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`angebot-download-${offer.id}`}
                      className={`w-full inline-flex items-center justify-center gap-2 py-3 rounded-full font-bold text-sm transition-all group ${
                        offer.highlight
                          ? "bg-emerald-500 text-white hover:bg-emerald-400 shadow-lg shadow-emerald-500/30"
                          : "bg-white text-slate-900 hover:bg-slate-100"
                      }`}
                    >
                      <Download className="w-4 h-4" />
                      PDF ansehen
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </a>
                    <a
                      href="#contact"
                      onClick={(e) => { e.preventDefault(); const el = document.getElementById("contact"); if (el) el.scrollIntoView({ behavior: "smooth" }); }}
                      className="w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-full text-xs font-semibold text-slate-300 hover:text-white border border-slate-600 hover:border-slate-400 transition"
                    >
                      Termin vereinbaren
                    </a>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Hinweis unten */}
        <div className="text-center mt-10 reveal reveal-delay-2">
          <p className="text-xs text-slate-500 leading-relaxed max-w-2xl mx-auto">
            Alle Angebote gelten nur solange der Vorrat reicht. Änderungen und Irrtümer vorbehalten. 
            Preise inkl. gesetzlicher MwSt. Für verbindliche Informationen kontaktiere Jennifer direkt.
          </p>
        </div>
      </div>
    </section>
  );
};

export default AngeboteSection;
